import { useCallback } from "react";
import { MultiUploadedFileResponse } from "@custom-types/file-upload-types";
import {
  createMutationAddPhotogrammetryInputToCaptureTree,
  MutationAddPhotogrammetryInputToCaptureTree,
  UploadedFileInfo,
} from "@faro-lotv/service-wires";
import { SdbProject } from "@custom-types/project-types";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { usePhotogrammetryApiClient } from "@api/photogrammetry-api/use-photogrammetry-api-client";
import { getImageDimensions } from "@pages/data-management/photogrammetry-data/photogrammetry-data-utils";
import { GUID } from "@faro-lotv/foundation";
import pLimit from "p-limit";
import { assertValue } from "@utils/assert-utils";
import { FileExt } from "@utils/file-ext";

export type MutationParams = {
  /** The files selected by the user */
  selectedFiles: FileList | (File | FileExt)[];
  /** The response from the upload */
  uploadedResponse: MultiUploadedFileResponse;
  /** Scan Id*/
  scanId: GUID;
  /** Scan Name*/
  scanName?: string;
};

type ReturnFunction = (params: MutationParams) => Promise<void>;

/**
 * The hook to prepare and send a mutation for adding photogrammetry input data to a project.
 * It processes selected files, retrieves necessary project elements, constructs the mutation,
 * and triggers a photogrammetry task using the photogrammetry API client.
 *
 * @param project - The project to which the mutation is sent.
 */
export function useSendMutation(project: SdbProject): ReturnFunction {
  const projectAPI = useProjectApiClient({ projectId: project.id });
  const currentUser = useAppSelector(currentUserSelector);
  const photogrammetryApiClient = usePhotogrammetryApiClient({
    projectId: project.id,
  });

  return useCallback(
    async ({
      selectedFiles,
      uploadedResponse,
      scanId,
      scanName,
    }: MutationParams): Promise<void> => {
      if (!currentUser?.id) {
        throw new Error("Missing required userId for photogrammetry task");
      }

      const fileMap = new Map<string, File>();
      Array.from(selectedFiles).forEach((file) => {
        fileMap.set(file.name, file);
      });

      // It seems that the browser memory limit can be hit when loading too many images at once.
      // Increase the limit as needed if 8 is too slow, didn't test much yet.
      const MAX_CONCURRENT_IMAGE_LOADS = 8;
      const limiter = pLimit(MAX_CONCURRENT_IMAGE_LOADS);

      const uploadedFileEntries = await Promise.all(
        uploadedResponse.successful.map(
          async (uploaded): Promise<UploadedFileInfo> => {
            const date = new Date();
            const localFile = fileMap.get(uploaded.fileName);

            let dimensions = { width: 0, height: 0 };
            if (localFile) {
              dimensions = await limiter(() => getImageDimensions(localFile));
            }

            return {
              uri: uploaded.downloadUrl,
              name: uploaded.fileName,
              pixelWidth: dimensions.width,
              pixelHeight: dimensions.height,
              duration: null,
              FileName: uploaded.fileName,
              fileSize: uploaded.fileSize,
              md5: uploaded.md5,
              createdAt: date.toUTCString(),
            };
          }
        )
      );
      const root = await projectAPI.getRootIElement();
      const mutation: MutationAddPhotogrammetryInputToCaptureTree =
        createMutationAddPhotogrammetryInputToCaptureTree({
          images2d: uploadedFileEntries,
          images360: [],
          videos2d: [],
          videos360: [],
          rootId: root.id,
          scanId: scanId,
          scanName: scanName,
        });
      const mutationScanId = assertValue(mutation.scan?.id, "Missing datasetId (entity ID) for photogrammetry task");
      
      // Verify that the scan ID returned by the mutation matches the input scan ID
      if (mutationScanId !== scanId) {
        throw new Error(`Scan ID mismatch: input scanId (${scanId}) differs from mutation.scan.id (${mutationScanId})`);
      }
      
      await projectAPI.applyMutations([mutation]);

      const payload = {
        projectId: project.id,
        userId: currentUser.id,
        scanId: mutationScanId,
      };

      await photogrammetryApiClient.triggerPhotogrammetryTask(payload);
    },
    [currentUser?.id, projectAPI, project.id, photogrammetryApiClient]
  );
}
